import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useUser, useUserContext } from '../../../../context/UserProvider'
import { fetchFleetReport, fetchReportTermLimits } from '../../../../api/newApi'
import LeasingReportTable from '../components/LeasingReportTable'
import { ConsolidateInvoiceSelect } from '../../../cars/components/V3/_shared/ConsolidateInvoiceSelect'
import type { Leasing } from '../../../../types'
import { ExcelExportButton } from '../components/ExcelExporter'
import { leasingReportColumns } from '../components/LeasingReportTable/excelUtils'
import ImprovedDatePicker from '../../../../components/fields/ImprovedDatePicker'
import { dateToValue, monthInputValue, valueToDate } from '../../../../utils/helpers/helpers.reports'

const clampMonth = (value: string, min: string, max: string) => {
  if (value < min) return min
  if (value > max) return max
  return value
}

const monthToTerm = (month: string) => `${month}-01`

const LeasingReport = () => {
  const { chosenCompany: company, consolidateInvoice } = useUserContext() as any
  const companyId = company?.id
  const contactId = useUser()?.user?.id
  const consolidateCode = consolidateInvoice?.ConsolidateCode || '_ALL_'
  const [term, setTerm] = useState('')

  const { data: termLimits } = useQuery(
    ['leasingReportTermLimits', companyId, contactId],
    () => fetchReportTermLimits({ companyId, contactId, type: 'fleet', isLeasing: true }),
    {
      enabled: !!companyId && !!contactId,
    },
  ) as { data?: { oldest: string; newest: string } }

  const oldestMonth = termLimits?.oldest ? monthInputValue(termLimits.oldest) : undefined
  const newestMonth = termLimits?.newest ? monthInputValue(termLimits.newest) : undefined

  useEffect(() => {
    if (!oldestMonth || !newestMonth) return

    const currentMonth = term ? monthInputValue(term) : newestMonth
    const nextTerm = monthToTerm(clampMonth(currentMonth, oldestMonth, newestMonth))

    if (nextTerm !== term) {
      setTerm(nextTerm)
    }
  }, [newestMonth, oldestMonth, term])

  const { data: response, status } = useQuery(
    ['fleetReport', companyId, term, contactId, 'leasing', consolidateCode],
    () =>
      fetchFleetReport({
        term,
        companyId,
        contactId,
        consolidatedAgreementID: consolidateCode,
        isLeasing: true,
      }),
    { enabled: !!companyId && !!contactId && !!term },
  )

  const items = (response && 'items' in response ? response.items : []) as Leasing[]

  const config = {
    columns: leasingReportColumns(),
    filename: `Leasingrapport_${term}.xlsx`,
    sheetName: `Leasingrapport_${term}`,
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:justify-between">
        <div className="flex flex-row justify-between items-center">
          <span className="text-xl font-medium text-primaryText">Leasingrapport</span>
          <div className="md:hidden">
            <ExcelExportButton data={items} config={config} />
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-end gap-2 w-full md:w-auto">
          <div className="flex flex-col gap-1 text-sm text-secondaryText flex-1 md:flex-none">
            <label htmlFor="leasing-report-term">Termin</label>
            <ImprovedDatePicker
              id="leasing-report-term"
              selected={valueToDate(term ? monthInputValue(term) : newestMonth || '', 'month')}
              language="nb"
              type="month"
              minDate={oldestMonth ? valueToDate(oldestMonth, 'month') : undefined}
              maxDate={newestMonth ? valueToDate(newestMonth, 'month') : undefined}
              disabled={!oldestMonth || !newestMonth}
              onChange={(date) => date && setTerm(monthToTerm(dateToValue(date, 'month')))}
            />
          </div>
          <div className="w-full md:w-auto md:flex-none md:self-end">
            <ConsolidateInvoiceSelect className="h-11 w-full" />
          </div>
          <div className="hidden md:block self-end">
            <ExcelExportButton className="h-11" data={items} config={config} />
          </div>
        </div>
      </div>

      <LeasingReportTable isLoading={status === 'loading'} data={items} />
    </div>
  )
}

export default LeasingReport
